import { Node, mergeAttributes } from '@tiptap/core';
import { syncIndentOnDom } from '../lib/post_block_indent.js';
import { createCaptionDOM, setupCaptionKeyboardHandling } from './CaptionExtension';

function normalizeUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('www.')) return `https://${trimmed}`;
  return trimmed;
}

function isValidYouTubeId(id) {
  // YouTube video IDs are typically 11 chars of [A-Za-z0-9_-]
  // (We keep this strict so we only embed when we are confident.)
  return typeof id === 'string' && /^[A-Za-z0-9_-]{11}$/.test(id);
}

function parseYouTubeId(urlString) {
  try {
    const url = new URL(normalizeUrl(urlString));
    const host = url.hostname.replace(/^www\./, '');

    // youtu.be/<id>
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return isValidYouTubeId(id) ? id : null;
    }

    const isYouTubeHost =
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com' ||
      host === 'youtube-nocookie.com';

    if (!isYouTubeHost) {
      return null;
    }

    // youtube.com/attribution_link?u=/watch%3Fv%3D<id>%26...
    if (url.pathname === '/attribution_link') {
      const u = url.searchParams.get('u');
      if (u) {
        try {
          const inner = new URL(u, 'https://youtube.com');
          const innerId = inner.searchParams.get('v');
          if (isValidYouTubeId(innerId)) return innerId;
        } catch {
          // ignore
        }
      }
    }

    // youtube.com/watch?v=<id>
    if (url.pathname === '/watch') {
      const id = url.searchParams.get('v');
      return isValidYouTubeId(id) ? id : null;
    }

    // youtube.com/embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(parts[0])) {
      const id = parts[1];
      return isValidYouTubeId(id) ? id : null;
    }

    return null;
  } catch {
    return null;
  }
}

function parseVimeoId(urlString) {
  try {
    const url = new URL(normalizeUrl(urlString));
    const host = url.hostname.replace(/^www\./, '');

    const isLikelyVimeoVideoId = (id) => typeof id === 'string' && /^\d{6,}$/.test(id);

    // vimeo.com/<id>
    if (host === 'vimeo.com') {
      const parts = url.pathname.split('/').filter(Boolean);

      // Common Vimeo URL shapes:
      // - /<id>
      // - /channels/<channel>/<id>
      // - /showcase/<showcase>/<id>
      // - /groups/<group>/videos/<id>
      // We support these by scanning for a likely numeric id from the end.
      const lastNumeric = [...parts].reverse().find(isLikelyVimeoVideoId);
      return lastNumeric || null;
    }

    // player.vimeo.com/video/<id>
    if (host === 'player.vimeo.com') {
      const parts = url.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('video');
      const id = idx >= 0 ? parts[idx + 1] : null;
      return isLikelyVimeoVideoId(id) ? id : null;
    }

    return null;
  } catch {
    return null;
  }
}

export function parseEmbedProvider(urlString) {
  const youtubeId = parseYouTubeId(urlString);
  if (youtubeId) return { provider: 'youtube', embedId: youtubeId };

  const vimeoId = parseVimeoId(urlString);
  if (vimeoId) return { provider: 'vimeo', embedId: vimeoId };

  return null;
}

export const EmbedVideoExtension = Node.create({
  name: 'embedVideo',
  group: 'block',
  selectable: true,
  draggable: true,
  atom: false,
  content: 'inline*',

  addAttributes() {
    return {
      provider: { default: null }, // 'youtube' | 'vimeo'
      embedId: { default: null },
      url: { default: null },
      style: { default: 'max-width:100%' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-tiptap-embed-video]',
        priority: 100,
        getAttrs: dom => {
          const embed = dom.querySelector('.plyr__video-embed,[data-plyr-provider][data-plyr-embed-id]');
          if (!embed) return false;
          return {
            provider: embed.getAttribute('data-plyr-provider') || null,
            embedId: embed.getAttribute('data-plyr-embed-id') || null,
            url: dom.getAttribute('data-url') || null,
            style: embed.getAttribute('style') || dom.getAttribute('style') || null,
          };
        }
      },
      // Legacy-ish support: allow raw plyr embed divs (not wrapped in figure)
      {
        tag: 'div[data-plyr-provider][data-plyr-embed-id]',
        priority: 50,
        getAttrs: dom => {
          if (dom.closest('figure')) return false;
          return {
            provider: dom.getAttribute('data-plyr-provider') || null,
            embedId: dom.getAttribute('data-plyr-embed-id') || null,
            url: dom.getAttribute('data-url') || null,
            style: dom.getAttribute('style') || null,
          };
        }
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { provider, embedId, url, style, ...rest } = HTMLAttributes;

    const figureAttrs = mergeAttributes(rest, {
      'data-tiptap-embed-video': '1',
      class: 'post-content-video',
    });
    if (url) figureAttrs['data-url'] = url;

    const embedDiv = [
      'div',
      mergeAttributes(
        {
          class: 'plyr__video-embed post-content-video__embed',
          'data-plyr-provider': provider,
          'data-plyr-embed-id': embedId,
          style,
        },
      ),
    ];

    return [
      'figure',
      figureAttrs,
      embedDiv,
      [
        'figcaption',
        {
          class: 'video-caption-container ps-post-style-caption',
        },
        0,
      ],
    ];
  },

  addNodeView() {
    return ({ node, editor }) => {
      const container = document.createElement('div');
      container.className = 'video-extension-container post-content-video';
      container.style.position = 'relative';
      syncIndentOnDom(container, node.attrs.indent);

      const embed = document.createElement('div');
      embed.className = 'plyr__video-embed post-content-video__embed';
      if (node.attrs.provider) embed.setAttribute('data-plyr-provider', node.attrs.provider);
      if (node.attrs.embedId) embed.setAttribute('data-plyr-embed-id', node.attrs.embedId);
      embed.style.cssText = 'max-width: 100%; display: block; margin: 0; padding: 0;';
      embed.contentEditable = false;

      // Editor placeholder (Plyr initializes only on show pages, not inside ProseMirror)
      const placeholder = document.createElement('div');
      placeholder.setAttribute('data-embed-placeholder', 'true');
      placeholder.style.cssText = [
        'width: 100%',
        'aspect-ratio: 16 / 9',
        'background: #0b0b0c',
        'border: 1px solid rgba(255,255,255,0.12)',
        'border-radius: 10px',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'padding: 16px',
        'box-sizing: border-box',
      ].join('; ');

      const message = document.createElement('div');
      message.style.cssText = [
        'max-width: 520px',
        'text-align: center',
        'color: rgba(255,255,255,0.92)',
        'font-family: Calibre, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
        'font-size: 14px',
        'line-height: 1.35',
      ].join('; ');

      const providerLabel =
        node.attrs.provider === 'youtube'
          ? 'YouTube'
          : node.attrs.provider === 'vimeo'
            ? 'Vimeo'
            : 'Video';

      message.textContent = `${providerLabel} embeds don’t play inside the editor. Preview on the post page after saving.`;
      placeholder.appendChild(message);
      embed.appendChild(placeholder);

      const { wrapper: captionWrapper, container: captionContainer } = createCaptionDOM('video-caption-container');
      setupCaptionKeyboardHandling(captionContainer, editor);

      container.appendChild(embed);
      container.appendChild(captionWrapper);

      return {
        dom: container,
        contentDOM: captionContainer,
        update: updatedNode => {
          if (updatedNode.type.name !== 'embedVideo') return false;
          syncIndentOnDom(container, updatedNode.attrs.indent);
          if (updatedNode.attrs.provider) embed.setAttribute('data-plyr-provider', updatedNode.attrs.provider);
          if (updatedNode.attrs.embedId) embed.setAttribute('data-plyr-embed-id', updatedNode.attrs.embedId);
          const nextProviderLabel =
            updatedNode.attrs.provider === 'youtube'
              ? 'YouTube'
              : updatedNode.attrs.provider === 'vimeo'
                ? 'Vimeo'
                : 'Video';
          message.textContent = `${nextProviderLabel} embeds don’t play inside the editor. Preview on the post page after saving.`;
          return true;
        },
        ignoreMutation: (mutation) => {
          // The editor should treat the embed container as non-editable/opaque.
          // Ignoring mutations here prevents ProseMirror from tearing down/recreating the node view.
          const target = mutation?.target;
          if (!target) return false;
          return embed.contains(target) || target === embed;
        },
        destroy: () => {
          // Nothing to clean up; embed is inert in the editor.
        },
      };
    };
  },
});
